import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { isGuestSession } from '../services/authFetch';

/**
 * #3406 — shown on a protected route while the stored session could not be
 * verified (AuthContext `authStatus === 'degraded'`: /auth/me failed with a
 * network error, 5xx or 429). Before this, ProtectedRoute saw
 * `isAuthenticated === false` and bounced the learner to /login even though
 * their session was probably fine.
 *
 * Three exits (the second is not offered to a guest — see #3580 below):
 *   - Try again: one immediate re-check (a background retry is also running
 *     on a bounded schedule; when it succeeds this panel is replaced by the
 *     page without any click).
 *   - Sign in again: logs out (the server revoke is attempted; the local
 *     token is cleared regardless) and goes to /login — the way out when the
 *     token really is dead but the server keeps answering 5xx. #3636: only
 *     the session the panel was shown for; a newer one from another tab is
 *     re-checked, not revoked.
 *   - Go to home page (#3599, #3685 item 1; D-200): for a guest AND a
 *     signed-in learner. Navigates to "/" and does nothing else: no logout,
 *     no revoke, no token clear. It is the exit a guest has (Sign in again
 *     is hidden for them) and the one a signed-in learner has when Sign in
 *     again only re-checks an adopted token during a continuing outage. A
 *     guest who then starts practising is protected from a silent new guest
 *     by AuthContext.loginAsGuest's re-check of the stored token.
 *
 * Every exit reports one `auth_restore_degraded_exit` event
 * ({exit, is_guest, completed}); see docs/subsystems/auth.md.
 */
export default function SessionRestoreDegraded() {
  const { t } = useTranslation();
  const { retryAuthRestore, logout, degradedToken, reportDegradedExit } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  // #3578 I: one flag per exit. A shared flag let a slow "Try again" disable
  // "Sign in again" too, so a stalled request left no exit at all. (The
  // restore request itself is now bounded, RESTORE_REQUEST_TIMEOUT_MS.)
  const [retrying, setRetrying] = useState(false);
  const [signingOut, setSigningOut] = useState(false);
  const guestSession = isGuestSession();

  const handleRetry = async () => {
    if (retrying || signingOut) return;
    setRetrying(true);
    const isGuest = isGuestSession();
    let completed = false;
    try {
      const status = await retryAuthRestore();
      completed = status === 'authenticated' || status === 'guest'; // AUTH_STATUS values (a mocked AuthContext exports useAuth only)
    } finally {
      setRetrying(false);
      reportDegradedExit?.({ exit: 'retry', is_guest: isGuest, completed });
    }
  };

  // #3599: deliberately nothing but a navigation. Never logout(), never a
  // revoke, never a token clear: the session this panel is about must still
  // be there when the connection comes back. Not gated on retrying/signingOut:
  // an exit must stay available while a stalled request is in flight.
  const handleGoHome = () => {
    reportDegradedExit?.({ exit: 'home', is_guest: isGuestSession(), completed: true });
    navigate('/');
  };

  const handleSignInAgain = async () => {
    // Re-read at click time: the stored token may have changed since render.
    if (signingOut || isGuestSession()) return;
    setSigningOut(true);
    let superseded = false;
    const isGuest = isGuestSession(); // read before logout() clears the token
    try {
      // #3636 item 4: end the session this panel is about (the token the
      // degraded restore read), never whatever storage holds at click time.
      // If another tab signed in meanwhile, logout() leaves that session
      // alone and re-checks it instead; the route then renders from the
      // verdict (the page, or ProtectedRoute's own redirect to /login).
      const result = await logout({ expectedToken: degradedToken ?? null });
      superseded = result?.superseded === true;
    } finally {
      setSigningOut(false);
      // completed: false when the token was superseded by another tab's
      // session (logout re-checked it instead): the panel stays up.
      reportDegradedExit?.({ exit: 'sign_in_again', is_guest: isGuest, completed: !superseded });
      if (!superseded) navigate('/login', { replace: true, state: { from: location } });
    }
  };

  // English defaults: i18n loads translation.json lazily with no Suspense,
  // and this panel appears exactly when the network is failing, so without
  // them it showed raw keys (auth.sessionRestore…) until the locale arrived.
  return (
    <div className="loading-container" data-testid="session-restore-degraded" role="alert">
      <h2>{t('auth.sessionRestoreDegradedTitle', "We couldn't check your sign-in")}</h2>
      <p>{t('auth.sessionRestoreDegradedBody', "You may still be signed in, but we couldn't reach the server to confirm it. We'll keep trying for a few seconds.")}</p>
      {/* #3599: say that leaving is safe, in the words that are true for this
          session. Guest: the practice is held by the guest session stored on
          this device. Signed in: going home does not end the sign-in. */}
      <p data-testid="session-restore-leave-safe">
        {guestSession
          ? t('auth.sessionRestoreLeaveSafeGuest', 'Your practice is kept on this device. You can safely go to the home page and come back when your connection is working.')
          : t('auth.sessionRestoreLeaveSafe', 'Going to the home page does not sign you out.')}
      </p>
      <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap', justifyContent: 'center' }}>
        <button type="button" className="btn-primary" onClick={handleRetry} disabled={retrying || signingOut}>
          {t('auth.sessionRestoreRetry', 'Try again')}
        </button>
        <button type="button" className="btn-secondary" onClick={handleGoHome}>
          {t('auth.sessionRestoreGoHome', 'Go to home page')}
        </button>
        {!guestSession && (
          <button type="button" className="btn-secondary" onClick={handleSignInAgain} disabled={signingOut}>
            {t('auth.sessionRestoreSignInAgain', 'Sign in again')}
          </button>
        )}
      </div>
    </div>
  );
}
